import { useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { usd, num, shortDate, relDate } from "../lib/format";
import type { Account, Segment } from "../lib/types";
import { Icon } from "../components/icons";
import { HealthBadge, Monogram, Empty } from "../components/ui";

const TODAY = new Date("2026-08-16T12:00:00Z");
const daysOut = (iso: string) => (new Date(iso).getTime() - TODAY.getTime()) / 86400000;

/**
 * Best-effort live predicate for a segment. Segments compose over the flexible
 * data model, so we map by id first and fall back to a generic filter reading.
 */
function predicateFor(seg: Segment): (a: Account) => boolean {
  switch (seg.id) {
    case "seg1":
      return (a) => daysOut(a.renewalDate) >= 0 && daysOut(a.renewalDate) <= 90;
    case "seg2":
      return (a) => a.attributes.segment === "Enterprise" && a.healthBand === "risk";
    case "seg3":
      return (a) => a.health >= 70 && a.expansionPotential >= 55;
    case "seg4":
      return (a) => a.attributes.sponsor_present === false;
    default:
      // Generic fallback: every filter must hold against a matching attribute.
      return (a) =>
        seg.filters.every((f) => {
          const v = f.field === "health" ? a.health : a.attributes[f.field];
          if (v === undefined) return true;
          const target = f.value;
          if (f.op === "<") return Number(v) < Number(target);
          if (f.op === ">=") return Number(v) >= Number(target);
          if (f.op === "is") return String(v) === target;
          return true;
        });
  }
}

export default function Segments() {
  const { ws } = useWorkspace();
  const [selectedId, setSelectedId] = useState<string>(ws.segments[0]?.id ?? "");

  const selected = ws.segments.find((s) => s.id === selectedId) ?? ws.segments[0];
  const matches = selected ? ws.accounts.filter(predicateFor(selected)) : [];

  return (
    <div className="page">
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Segments</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Live cohorts</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>
            Compose cohorts from any field in your model — standard or custom. Segments are queries, not lists:
            they recompute the moment the underlying data changes.
          </p>
        </div>
        <button className="btn btn--ghost"><Icon name="plus" size={15} /> New segment</button>
      </div>

      {/* Segment cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16, marginBottom: 24 }}>
        {ws.segments.map((seg) => {
          const active = seg.id === selectedId;
          return (
            <button
              key={seg.id}
              onClick={() => setSelectedId(seg.id)}
              className="card"
              style={{
                textAlign: "left", cursor: "pointer", padding: 0,
                borderColor: active ? "var(--corona)" : undefined,
                boxShadow: active ? "0 0 0 1px var(--corona)" : undefined,
              }}
            >
              <div className="card__bd">
                <div className="row" style={{ gap: 9, marginBottom: 6 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: seg.color, flex: "none" }} />
                  <b style={{ fontSize: 15, fontWeight: 600 }}>{seg.name}</b>
                </div>
                <p className="muted" style={{ fontSize: 13, minHeight: 38 }}>{seg.description}</p>
                <div className="row wrap" style={{ gap: 8, margin: "12px 0" }}>
                  {seg.filters.map((f, i) => (
                    <span className="filter-pill" key={i}>
                      <span className="k">{f.field}</span>
                      <span className="op">{f.op}</span>
                      <span>{f.value}</span>
                    </span>
                  ))}
                </div>
                <div className="row between" style={{ borderTop: "1px solid var(--line-soft)", paddingTop: 12 }}>
                  <span style={{ fontSize: 13 }}><b className="num">{seg.count}</b> <span className="faint">accounts</span></span>
                  <span className="num" style={{ fontSize: 13, fontWeight: 600 }}>{usd(seg.arr)}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Live preview of the selected segment */}
      {selected && (
        <div className="card">
          <div className="card__hd">
            <div>
              <div className="row" style={{ gap: 9 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: selected.color, flex: "none" }} />
                <h3>{selected.name}</h3>
              </div>
              <span className="sub">Live preview against {num(ws.accounts.length)} accounts</span>
            </div>
            <span className="tag">{matches.length} matched</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Health</th>
                  <th className="num">ARR</th>
                  <th>Renewal</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/accounts/${a.id}`} className="acct-cell">
                        <Monogram name={a.name} />
                        <div>
                          <b>{a.name}</b>
                          <small>{a.industry} · {a.region}</small>
                        </div>
                      </Link>
                    </td>
                    <td><HealthBadge band={a.healthBand} /></td>
                    <td className="num">{usd(a.arr)}</td>
                    <td>
                      <b style={{ fontSize: 13 }}>{relDate(a.renewalDate)}</b>
                      <small className="faint" style={{ display: "block" }}>{shortDate(a.renewalDate)}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {matches.length === 0 && <Empty>No accounts currently match this segment.</Empty>}
          </div>
          <div className="card__bd" style={{ borderTop: "1px solid var(--line-soft)", color: "var(--faint)", fontSize: 12.5, display: "flex", gap: 8, alignItems: "center" }}>
            <Icon name="refresh" size={14} /> Segments update live as data changes — no rebuilds, no exports.
          </div>
        </div>
      )}
    </div>
  );
}
